import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, MenuItem, TextField, Typography } from '@mui/material';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useAuth } from '../../context/AuthContext';
import { brand } from '../../theme';
import { formatBytes } from '../../utils/format';
import { blobByteSource } from '../../utils/media/byteSource';
import { RecordingSyncResult, syncRecordingBySound } from '../../utils/media/recordingSync';
import {
  AudioRecording,
  RECORDING_ACCEPT,
  fetchAudioRecordings,
  inspectRecordingFile,
  uploadAudioRecording,
} from '../../services/audioRecordingApi';
import { debugLog } from '../../utils/debugLog';
import { friendlyErrorMessage } from '../Library/friendlyError';
import { formatVideoDuration, joinMeta } from '../Library/libraryFormat';
import { DEFAULT_RECORDING_LABEL, recordingDisplayName } from './separateAudio';

/** The set's separate recording as the set-up screen edits it. */
export interface SeparateAudioDraft {
  /** Null while the file is still uploading. */
  videoId: string | null;
  label: string;
  videoName: string;
  videoUrl: string | null;
  sizeBytes: number;
  durationMs: number;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs: number | null;
}

export interface SeparateAudioMaster {
  url: string;
  sourceInMs: number;
  durationMs: number;
}

interface SeparateAudioSectionProps {
  master: SeparateAudioMaster | null;
  value: SeparateAudioDraft | null;
  onChange: (next: SeparateAudioDraft | null) => void;
  /** Told while a file uploads or the sound is being matched, so the set isn't saved half-done. */
  onBusyChange?: (busy: boolean) => void;
  disabled?: boolean;
  /** Start lining up a newly added recording straight away (default true). */
  autoSync?: boolean;
}

type SyncState =
  | { status: 'idle' }
  | { status: 'running'; fraction: number; stage: 'start' | 'end' }
  | { status: 'done'; result: RecordingSyncResult; adjusted?: boolean }
  | { status: 'cancelled' };

const parseDurationMs = (duration: string): number => {
  const parts = duration.split(':').map((part) => parseInt(part, 10) || 0);
  return parts.reduce((total, part) => total * 60 + part, 0) * 1000;
};

/** "0:42.50" */
const formatSeconds = (ms: number): string => {
  const total = Math.max(0, ms) / 1000;
  const minutes = Math.floor(total / 60);
  return `${minutes}:${(total - minutes * 60).toFixed(2).padStart(5, '0')}`;
};

/** "12.48 s", or "1:12.34" past a minute. */
const formatOffset = (ms: number): string => (Math.abs(ms) >= 60000 ? formatSeconds(Math.abs(ms)) : `${(Math.abs(ms) / 1000).toFixed(2)} s`);

/** Mirrors the server's checks so problems show by the fields, before the save. */
export const separateAudioProblem = (draft: SeparateAudioDraft | null): string | null => {
  if (!draft) return null;
  const limit = draft.durationMs + 1000;
  if (!Number.isFinite(draft.timelineStartMs) || draft.timelineStartMs < 0) return 'The start must be zero or more.';
  if (!Number.isFinite(draft.sourceInMs) || draft.sourceInMs < 0) return 'Trim start must be zero or more.';
  if (draft.durationMs > 0 && draft.sourceInMs >= limit) {
    return `Trim start is past the end of the recording (${formatSeconds(draft.durationMs)}).`;
  }
  if (draft.sourceOutMs !== null && draft.sourceOutMs <= draft.sourceInMs) return 'Trim end must be after trim start.';
  return null;
};

/** What lining up by sound found, in words. */
const describeResult = (state: Extract<SyncState, { status: 'done' }>): { severity: 'success' | 'warning' | 'info'; text: string } => {
  const { start } = state.result;
  if (start.status === 'matched') {
    const offsetMs = Math.round(start.estimate.offsetS * 1000);
    const when = offsetMs >= 0
      ? `The recording started ${formatOffset(offsetMs)} after the master.`
      : `The recording started ${formatOffset(offsetMs)} before the master, so that much is trimmed from its start.`;
    const match = start.estimate.quality === 'strong' ? 'Strong match.' : 'Good match, worth a quick listen.';
    return state.adjusted
      ? { severity: 'info', text: `Lined up by sound, then adjusted by hand. ${when}` }
      : { severity: 'success', text: `Lined up by sound. ${when} ${match}` };
  }
  if (start.status === 'weak') {
    return { severity: 'warning', text: 'The sound didn’t match clearly enough to be sure, so nothing was changed. Set the start below by hand.' };
  }
  return { severity: 'warning', text: start.reason.replace('Set this one by hand.', 'Set the start below by hand.') };
};

/** The drift check's finding, when it is worth saying. */
const describeDrift = (result: RecordingSyncResult): { severity: 'warning' | 'info'; text: string } | null => {
  const drift = result.drift;
  if (!drift || drift.status === 'skipped') return null;
  if (!drift.noticeable) return { severity: 'info', text: 'Checked near the end too: the recording stays in sync with the master.' };
  const by = `${(Math.abs(drift.driftMs) / 1000).toFixed(2)} s`;
  const minutes = Math.max(1, Math.round(drift.spanS / 60));
  return {
    severity: 'warning',
    text: `The recording drifts: it’s lined up at the start, but by the end (about ${minutes} minutes in) its sound is ${by} ${
      drift.driftMs > 0 ? 'ahead of' : 'behind'
    } the picture. Recorders and cameras keep slightly different time. Your editor can stretch the recording to fit.`,
  };
};

const fromRecording = (recording: AudioRecording): SeparateAudioDraft => ({
  videoId: recording.id,
  label: DEFAULT_RECORDING_LABEL,
  videoName: recording.name,
  videoUrl: recording.url,
  sizeBytes: recording.size,
  durationMs: parseDurationMs(recording.duration || '0:00'),
  timelineStartMs: 0,
  sourceInMs: 0,
  sourceOutMs: null,
});

/**
 * "Separate audio (optional)" in the set-up's Sync step: upload or pick the
 * recording from a recorder or mixer, line it up with the master by sound
 * (and check it hasn't drifted by the end), and fine-tune it by hand.
 */
const SeparateAudioSection: React.FC<SeparateAudioSectionProps> = ({
  master,
  value,
  onChange,
  onBusyChange,
  disabled = false,
  autoSync = true,
}) => {
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [library, setLibrary] = useState<AudioRecording[] | null>(null);
  const [upload, setUpload] = useState<{ name: string; percent: number } | null>(null);
  const [sync, setSync] = useState<SyncState>({ status: 'idle' });
  const uploadAbortRef = useRef<AbortController | null>(null);
  const syncAbortRef = useRef<AbortController | null>(null);
  // The local file, while it's around: lining it up reads from disk, not the network.
  const localFileRef = useRef<File | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  /**
   * Changes the draft from an async finish (an upload, a match). Two can land
   * before the next render, so each builds on the last rather than on props.
   */
  const patch = useCallback((change: Partial<SeparateAudioDraft>, only?: (draft: SeparateAudioDraft) => boolean) => {
    const latest = valueRef.current;
    if (!latest || (only && !only(latest))) return;
    const next = { ...latest, ...change };
    valueRef.current = next;
    onChange(next);
  }, [onChange]);
  const replace = useCallback((next: SeparateAudioDraft | null) => {
    valueRef.current = next;
    onChange(next);
  }, [onChange]);

  const busy = upload !== null || sync.status === 'running';
  const busyChangeRef = useRef(onBusyChange);
  busyChangeRef.current = onBusyChange;
  useEffect(() => {
    busyChangeRef.current?.(busy);
  }, [busy]);
  useEffect(() => () => {
    uploadAbortRef.current?.abort();
    syncAbortRef.current?.abort();
    busyChangeRef.current?.(false);
  }, []);

  // A transfer that may have run for many minutes: ask before the tab closes.
  useEffect(() => {
    if (!upload) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [upload]);

  // A different master makes the last match describe another file; the offset stays.
  const masterUrl = master?.url ?? null;
  const lastMasterRef = useRef(masterUrl);
  useEffect(() => {
    if (lastMasterRef.current === masterUrl) return;
    lastMasterRef.current = masterUrl;
    syncAbortRef.current?.abort();
    setSync({ status: 'idle' });
  }, [masterUrl]);

  // The library picker is a shortcut: when it can't load, uploading still works.
  const loadLibrary = useCallback(async () => {
    try {
      setLibrary(await fetchAudioRecordings());
    } catch (error) {
      debugLog('Recordings could not be listed:', error);
      setLibrary([]);
    }
  }, []);

  useEffect(() => {
    if (!value && library === null) void loadLibrary();
  }, [value, library, loadLibrary]);

  const runSync = useCallback(async (draft: SeparateAudioDraft) => {
    if (!master?.url) return;
    syncAbortRef.current?.abort();
    const controller = new AbortController();
    syncAbortRef.current = controller;
    setSync({ status: 'running', fraction: 0, stage: 'start' });
    const local = localFileRef.current;
    try {
      const result = await syncRecordingBySound(
        { key: 'A', url: master.url, sourceInMs: master.sourceInMs },
        local ? { key: 'AUDIO', source: blobByteSource(local), sourceInMs: draft.sourceInMs } : { key: 'AUDIO', url: draft.videoUrl ?? '', sourceInMs: draft.sourceInMs },
        {
          signal: controller.signal,
          onProgress: ({ fraction }) => {
            if (!controller.signal.aborted) setSync((current) => (current.status === 'running' ? { ...current, fraction } : current));
          },
          onDriftCheck: () => {
            if (!controller.signal.aborted) setSync((current) => (current.status === 'running' ? { ...current, stage: 'end' } : current));
          },
        }
      );
      if (controller.signal.aborted) return;
      if (result.start.status === 'matched') {
        const { timelineStartMs, sourceInMs } = result.start.timing;
        patch({ timelineStartMs, sourceInMs }, (latest) => latest.videoName === draft.videoName);
      }
      setSync({ status: 'done', result });
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') {
        if (syncAbortRef.current === controller) setSync({ status: 'cancelled' });
        return;
      }
      console.error('Lining up the recording failed:', error);
      setSync({ status: 'idle' });
      setProblem('Lining up by sound stopped unexpectedly. Set the start by hand, or try again.');
    } finally {
      if (syncAbortRef.current === controller) syncAbortRef.current = null;
    }
  }, [master, patch]);

  const handleFile = async (file: File | undefined) => {
    if (!file || disabled || busy) return;
    setProblem(null);
    if (!user) {
      setProblem('Sign in to upload a recording.');
      return;
    }
    let info;
    try {
      info = await inspectRecordingFile(file);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'PithPlay can’t read this recording.');
      return;
    }
    const draft: SeparateAudioDraft = {
      videoId: null,
      label: DEFAULT_RECORDING_LABEL,
      videoName: file.name,
      videoUrl: null,
      sizeBytes: file.size,
      durationMs: Math.round(info.durationSeconds * 1000),
      timelineStartMs: 0,
      sourceInMs: 0,
      sourceOutMs: null,
    };
    localFileRef.current = file;
    replace(draft);
    // Lining up reads the local file, so it runs alongside the upload.
    if (autoSync && master?.url) void runSync(draft);

    const controller = new AbortController();
    uploadAbortRef.current = controller;
    setUpload({ name: file.name, percent: 0 });
    try {
      const token = await user.getIdToken(true);
      const recording = await uploadAudioRecording(file, info, token, (percent) => {
        if (!controller.signal.aborted) setUpload({ name: file.name, percent });
      }, controller.signal);
      patch({ videoId: recording.id, videoUrl: recording.url }, (latest) => latest.videoName === file.name && latest.videoId === null);
      setLibrary((current) => (current ? [recording, ...current.filter((item) => item.id !== recording.id)] : current));
    } catch (error) {
      if ((error as { name?: string })?.name === 'AbortError') return;
      syncAbortRef.current?.abort();
      localFileRef.current = null;
      replace(null);
      setSync({ status: 'idle' });
      setProblem(friendlyErrorMessage(error, 'The recording didn’t upload. Check your connection and try again.'));
    } finally {
      if (uploadAbortRef.current === controller) uploadAbortRef.current = null;
      setUpload(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const cancelUpload = () => {
    uploadAbortRef.current?.abort();
    syncAbortRef.current?.abort();
    localFileRef.current = null;
    setUpload(null);
    setSync({ status: 'idle' });
    replace(null);
  };

  const pickFromLibrary = (id: string) => {
    const recording = library?.find((item) => item.id === id);
    if (!recording) return;
    setProblem(null);
    localFileRef.current = null;
    const draft = fromRecording(recording);
    replace(draft);
    if (autoSync && master?.url) void runSync(draft);
  };

  const remove = () => {
    syncAbortRef.current?.abort();
    localFileRef.current = null;
    setSync({ status: 'idle' });
    setProblem(null);
    replace(null);
  };

  const editTiming = (change: Partial<SeparateAudioDraft>) => {
    if (!value) return;
    patch(change);
    setSync((current) => (current.status === 'done' && current.result.start.status === 'matched' ? { ...current, adjusted: true } : current));
  };

  const timingProblem = separateAudioProblem(value);
  const masterMs = master?.durationMs ?? 0;
  const endsOnTimelineMs = value ? value.timelineStartMs + Math.max(0, (value.sourceOutMs ?? value.durationMs) - value.sourceInMs) : 0;
  const running = sync.status === 'running' ? sync : null;
  const done = sync.status === 'done' ? sync : null;
  const drift = done ? describeDrift(done.result) : null;

  return (
    <Box
      component="section"
      aria-labelledby="separate-audio-title"
      data-testid="separate-audio-section"
      sx={{ mt: 3, p: 2, borderRadius: 2, border: `1px solid ${brand.border}`, bgcolor: 'background.default' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <AudioFileOutlinedIcon sx={{ color: 'primary.main', mt: 0.25 }} aria-hidden="true" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id="separate-audio-title" component="h3" variant="subtitle1" sx={{ fontWeight: 600 }}>
            Separate audio (optional)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Recorded the sound on its own recorder or mixer? Add that file (WAV, MP3, M4A, AAC or AIFF) and PithPlay
            lines it up with the master by sound. It isn’t an angle, and it becomes the set’s sound.
          </Typography>
        </Box>
      </Box>

      {problem && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setProblem(null)}>{problem}</Alert>}

      {!value && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <input
            ref={inputRef}
            type="file"
            accept={RECORDING_ACCEPT}
            hidden
            data-testid="separate-audio-input"
            onChange={(event) => void handleFile(event.target.files?.[0])}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={disabled || busy}
          >
            Upload recording
          </Button>
          {library && library.length > 0 && (
            <TextField
              select
              size="small"
              label="Or choose from your library"
              value=""
              onChange={(event) => pickFromLibrary(event.target.value)}
              disabled={disabled || busy}
              sx={{ minWidth: 260 }}
            >
              {library.map((recording) => (
                <MenuItem key={recording.id} value={recording.id}>
                  {joinMeta([recording.name, formatVideoDuration(recording.duration)])}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      )}

      {value && (
        <Box sx={{ mt: 2 }} data-testid="separate-audio-chosen">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap title={value.videoName}>
              {value.videoName}
              <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                {' · '}
                {joinMeta([value.durationMs > 0 ? formatSeconds(value.durationMs).replace(/\.\d+$/, '') : null, value.sizeBytes > 0 ? formatBytes(value.sizeBytes) : null])}
              </Box>
            </Typography>
            {!upload && !running && (
              <Button
                size="small"
                startIcon={<GraphicEqOutlinedIcon />}
                onClick={() => void runSync(value)}
                disabled={disabled || !master?.url || (!value.videoUrl && !localFileRef.current)}
              >
                {sync.status === 'done' ? 'Sync again' : 'Sync by sound'}
              </Button>
            )}
            {upload ? (
              <Button size="small" color="inherit" onClick={cancelUpload}>Cancel upload</Button>
            ) : (
              <Button size="small" color="inherit" startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />} onClick={remove} disabled={disabled}>
                Remove
              </Button>
            )}
          </Box>

          {upload && (
            <Box sx={{ mt: 1.5 }} role="status" aria-live="polite">
              <LinearProgress variant="determinate" value={upload.percent} aria-label="Recording upload progress" />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Uploading {upload.name}… {upload.percent}%. Keep this page open until it finishes.
              </Typography>
            </Box>
          )}
          {running && (
            <Box sx={{ mt: 1.5 }} role="status" aria-live="polite">
              <LinearProgress variant="determinate" value={Math.round(running.fraction * 100)} aria-label="Line up by sound progress" />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {running.stage === 'end' ? 'Checking the end of the recording stays in sync…' : 'Listening to the recording and the master…'}{' '}
                {Math.round(running.fraction * 100)}%
                {' '}
                <Button size="small" color="inherit" onClick={() => syncAbortRef.current?.abort()}>Stop</Button>
              </Typography>
            </Box>
          )}
          {done && (() => {
            const { severity, text } = describeResult(done);
            return <Alert severity={severity} sx={{ mt: 1.5 }} data-testid="separate-audio-result">{text}</Alert>;
          })()}
          {drift && <Alert severity={drift.severity} sx={{ mt: 1 }} data-testid="separate-audio-drift">{drift.text}</Alert>}
          {sync.status === 'cancelled' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }} role="status">
              Stopped. Set the start below by hand, or sync again.
            </Typography>
          )}

          <Box sx={{ display: 'grid', gap: 2, mt: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' } }}>
            <TextField
              size="small"
              label="Label"
              value={value.label}
              onChange={(event) => patch({ label: event.target.value })}
              placeholder="e.g. Zoom H6"
              inputProps={{ maxLength: 80 }}
              disabled={disabled}
            />
            <TextField
              size="small"
              label="Starts at (s)"
              type="number"
              value={value.timelineStartMs / 1000}
              onChange={(event) => {
                const seconds = parseFloat(event.target.value);
                if (!Number.isNaN(seconds) && seconds >= 0) editTiming({ timelineStartMs: Math.round(seconds * 1000) });
              }}
              inputProps={{ min: 0, step: 0.01, 'aria-label': 'Separate audio start on the master’s timeline, in seconds' }}
              helperText="On the master’s timeline"
              disabled={disabled}
            />
            <TextField
              size="small"
              label="Trim start (s)"
              type="number"
              value={value.sourceInMs / 1000}
              onChange={(event) => {
                const seconds = parseFloat(event.target.value);
                if (!Number.isNaN(seconds) && seconds >= 0) editTiming({ sourceInMs: Math.round(seconds * 1000) });
              }}
              inputProps={{ min: 0, step: 0.01, 'aria-label': 'Separate audio trim start, in seconds' }}
              helperText="Seconds into the recording"
              disabled={disabled}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            If the recorder started before the master, leave the start at 0 and set Trim start to how many seconds
            earlier it started.
          </Typography>
          {value.durationMs > 0 && masterMs > 0 && endsOnTimelineMs < masterMs - 1000 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
              The recording ends at {formatSeconds(endsOnTimelineMs)} on the timeline, before the master does. After
              that the set has no sound.
            </Typography>
          )}
          {value.timelineStartMs > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
              Before {formatSeconds(value.timelineStartMs)} the set has no sound.
            </Typography>
          )}
          {timingProblem && <Alert severity="error" sx={{ mt: 1.5 }}>{timingProblem}</Alert>}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {recordingDisplayName(value)} is the set’s sound in the studio, the transcript and the editor’s rough cut.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SeparateAudioSection;
